import { Box, Grid, Typography } from "@mui/material";
import { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CreateEntityButton } from "../../components/CreateEntityButton";
import { PaginationQueryPackage } from "../../models/API/QueryParams/PaginationQueryPackage";
import { PermissionEnum } from '../Permission/PermissionEnum';
import { AbilityContext, Can } from '../UserApi/logic/Can';
import { UserContext } from '../UserApi/logic/FetchUser';
import { DnsCreateTXTRequest, DnsCreateTXTResponse } from "../../models/dns";
import EasyForm from "../../components/Forms/EasyForm";
import EasyFormColumn from "../../components/Forms/EasyFormColumn";
import { FormType } from "../../components/Forms/EasyFormMany";
import { AutocompleteFieldRender } from "../../components/Forms/Renders/AutocompleteFieldRender";
import { TextRenderer } from "../../components/Forms/Renders/TextRenderer";
import { OrganisationContext } from "../../components/Layout/OrganisationPicker";

import { RecordInfoDialog } from "../DnsAPI/dnsInfoDialog/DnsInfoDialog";
import * as Yup from 'yup';
import { useGetDnsSuffixsByOrganisationIdQuery } from "../OrganisationApi/redux/dnsSuffixApiSlice";
import { DNSSuffix } from "../../models/DNSSuffix";
import getEnvironment from "../../env";

import { TextFieldRender } from "../../components/Forms/Renders/TextFieldRender";
import { useAddRecordMutation } from "./redux/dnsApiSlice";
import { useFormikContext } from "formik";


export const CreateDNSButton = () => {
    const [open, setOpen] = useState<boolean>(false)
    const [submittedDNS, setSubmittedDNS] = useState<DnsCreateTXTResponse | undefined>(undefined)

    const organisationContext = useContext(OrganisationContext)?.organisation

    return (
        <Can I={PermissionEnum.DNS_RECORD_WRITE} a={organisationContext} >
            <CreateEntityButton

                tooltip="Create record"
                open={open}
                setOpen={setOpen}
                onClose={() => setSubmittedDNS(undefined)}
            >
                {submittedDNS === undefined ?

                    <CreateDNSButtonContent afterSaveAction={(response) => setSubmittedDNS(response)} />
                    :
                    <RecordInfoDialog recordUuid={submittedDNS.uuid!} afterDeleteAction={() => setOpen(false)} />
                }
            </CreateEntityButton>
        </Can >
    )
}

export interface CreateDNSButtonContentProps {
    afterSaveAction: (response?: DnsCreateTXTResponse) => void
}
export const CreateDNSButtonContent = (props: CreateDNSButtonContentProps) => {
    const ability = useContext(AbilityContext);
    const { t } = useTranslation();

    const userContext = useContext(UserContext);
    const organisationContext = useContext(OrganisationContext);
    const organisation = organisationContext?.organisation ?? userContext?.organisation

    const allDnsSuffixes = useGetDnsSuffixsByOrganisationIdQuery({
        pagination: new PaginationQueryPackage(),
        uuid: organisation?.uuid
    }, { skip: !organisation?.uuid });

    const suffixes = allDnsSuffixes.data;

    const SignupSchema = Yup.object().shape({
        prefix: Yup.string()
            .transform((value, originalValue) => (originalValue === "" ? undefined : value))
            .optional(),

        suffix: Yup.string()
            .nullable()
            .transform((_, originalValue) => {
                if (typeof originalValue === 'object' && originalValue !== null) {
                    return originalValue.uuid;
                }
                return originalValue;
            })
            .required(t("Suffix is required"))
            .test(
                "is-valid-suffix",
                t("Invalid suffix"),
                (value) => {
                    if (!value) return false;
                    return suffixes?.some((suffix) => suffix.uuid === value) ?? false;
                }
            ),

        value: Yup.string().required(t("Value is required"))
    });

    const DynamicFqdnPreview = () => {
        const { values } = useFormikContext<DnsCreateTXTRequest>();
        const domain = getEnvironment().REACT_APP_DSDN_DOMAIN;

        const prefix = values.prefix ? `${values.prefix}.` : "";

        const runtimeSuffix = values.suffix as any;
        const suffixText = runtimeSuffix?.dns_suffix ? runtimeSuffix.dns_suffix : "<suffix>";

        return <>{prefix}{suffixText}.{domain}</>
    };

    const formColumns = useMemo(() => [
        new EasyFormColumn("", "", [
            new TextFieldRender({
                displayText: t("Prefix"),
                fieldName: "prefix",
                type: "text",
                uniqueId: "prefix_input_id",
            }),
            new AutocompleteFieldRender({
                displayText: t("Suffix"),
                fieldName: "suffix",
                getOptionLabelFunc: (option: DNSSuffix) => option ? `${option.dns_suffix}` : "",
                options: suffixes ?? [],
                uniqueId: "suffix_input_id"
            }),
            new TextFieldRender({
                displayText: t("Value"),
                fieldName: "value",
                type: "text",
                uniqueId: "value_input_id"
            }),
            new TextRenderer({
                disabled: true,
                displayText: "FQDN",
                fieldName: "fqdn_preview",
                uniqueId: "fqdn_preview_id",
                render: () => <DynamicFqdnPreview />
            }),
        ])
    ], [t, suffixes]);

    return (
        <Box>
            <Grid container justifyContent="center">
                <Grid item xs={6}>
                    <Typography variant="h6">{t("Create TXT record")}</Typography>
                </Grid>
            </Grid>
            <Grid container justifyContent="center">
                <Grid item xs={6}>
                    <EasyForm<any>
                        isAllowedToEdit={() => ability.can(PermissionEnum.AUTHORISATION_WRITE, organisation)}
                        editModeAlways={true}
                        arrayPath=""
                        SignupSchema={SignupSchema}
                        createNew={() => new DnsCreateTXTRequest()}
                        columns={formColumns}
                        formType={FormType.INFORMATION}
                        idToFetch={"no_id"}
                        getFieldRows={() => []}
                        saveMutation={useAddRecordMutation}
                        afterSaveAction={props.afterSaveAction}
                    />
                </Grid>
            </Grid>
        </Box>
    )
}